// ============================================================
// fondos/efectos/blobs-morph.js — «Blobs»: manchas orgánicas que se deforman (efecto de LIENZO a
// resolución BAJA)
//
// Portado de `vendor/bg/blobs-morph-bg.js` en la F3 del P3 (§3.1-bis, 30-09-2026): cada blob es un
// LAZO cerrado de 9 vértices cuyo radio oscila, trazado con curvas por los puntos medios y relleno
// con un degradado radial. El lienzo va a ⅓ de la resolución de la página (`nitidez: 'baja'`): al
// estirarlo, el filo queda suave y pintar sale barato. Respecto a la clase de siempre:
//   · el segundo color es el de la PALETA (`params.color2`, `fondos/paleta.js`), no el gris pizarra
//     fijo (E4);
//   · las alfas son fracciones del tope del catálogo (`alfaMax`, legibilidad), no un 0,4/0,55 fijo;
//   · el reloj es el del motor (`t`) y la deriva avanza por pasos (`dt`), no por fotograma pintado;
//   · la SUAVIDAD (`blur`) es el filo: a 0 el de siempre; al subir, el degradado se recoge dentro
//     del contorno y el filo se funde.
// En capas CSS (el intento del 29-09) un blob era una unión de elipses: «píldoras chocándose y
// fusionándose» (el dueño, 30-09). Un contorno orgánico necesita un trazo.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM, sin reloj y sin estado de módulo.
// ============================================================

import { pasos, rgbDeBase, rgba, interpolar } from './comun.js';

/** Vértices del lazo de cada blob. */
const VERTICES = 9;

/** Ritmo de deformación por punto de velocidad (rad/s de un vértice medio). */
const RITMO = 0.36;

/** La deriva no acerca el centro de un blob a menos de esto del borde (fracción de la sección). */
const MARGEN = 0.12;

/** El filo con la suavidad `suavidad` (0-100): radio del degradado (× el del blob) y su parada media. */
export function filoDeSuavidad(suavidad) {
    const s = (typeof suavidad === 'number' && suavidad > 0) ? Math.min(suavidad, 100) / 100 : 0;
    return { radio: 1.3 - 0.3 * s, medio: 0.6 - 0.1 * s };
}

export default {
    nitidez: 'baja',
    divisor: 3,

    crear(ctx, e) {
        const p = e.params;
        // Densidad 1-10 → 2-5 blobs; la velocidad es el ritmo al que se deforman.
        const n = Math.round(interpolar(p.density, 5, 2, 5));
        const ritmo = (p.speed != null ? p.speed : 5) * RITMO;
        const c1 = rgbDeBase(e.c1);
        const c2 = p.color2 ? rgbDeBase(p.color2) : c1;
        const blobs = [];
        for (let i = 0; i < n; i++) {
            const verts = [];
            for (let k = 0; k < VERTICES; k++) {
                verts.push({
                    ang: (k / VERTICES) * Math.PI * 2,
                    fase: e.azar() * Math.PI * 2,
                    vel: (0.4 + e.azar() * 0.8) * ritmo,
                    amp: 0.14 + e.azar() * 0.14,
                });
            }
            blobs.push({
                cx: 0.3 + e.azar() * 0.4,
                cy: 0.3 + e.azar() * 0.4,
                vx: (e.azar() - 0.5) * 0.08 * ritmo,
                vy: (e.azar() - 0.5) * 0.08 * ritmo,
                r: 0.26 + e.azar() * 0.14,
                color: i % 2 ? c2 : c1,
                verts: verts,
            });
        }
        return { w: e.w, h: e.h, alfa: e.alfa, filo: filoDeSuavidad(p.blur), blobs: blobs };
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t, dt) {
        const k = pasos(dt);
        const time = t * 0.001;
        const minDim = Math.min(s.w, s.h);
        s.blobs.forEach(function (b) {
            if (k > 0) {
                b.cx += b.vx * k / 100;
                b.cy += b.vy * k / 100;
                // Rebota sin quedarse enganchado al borde (un `dt` largo lo sacaría más de un paso).
                if (b.cx < MARGEN) b.vx = Math.abs(b.vx);
                else if (b.cx > 1 - MARGEN) b.vx = -Math.abs(b.vx);
                if (b.cy < MARGEN) b.vy = Math.abs(b.vy);
                else if (b.cy > 1 - MARGEN) b.vy = -Math.abs(b.vy);
            }
            const cx = b.cx * s.w;
            const cy = b.cy * s.h;
            const radio = b.r * minDim;
            const pts = b.verts.map(function (v) {
                const rr = radio * (1 + v.amp * Math.sin(time * v.vel + v.fase));
                return { x: cx + Math.cos(v.ang) * rr, y: cy + Math.sin(v.ang) * rr };
            });
            // Contorno cerrado suave: curvas por los puntos medios.
            const n = pts.length;
            ctx.beginPath();
            ctx.moveTo((pts[n - 1].x + pts[0].x) / 2, (pts[n - 1].y + pts[0].y) / 2);
            for (let i = 0; i < n; i++) {
                const a = pts[i];
                const z = pts[(i + 1) % n];
                ctx.quadraticCurveTo(a.x, a.y, (a.x + z.x) / 2, (a.y + z.y) / 2);
            }
            ctx.closePath();
            const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radio * s.filo.radio);
            g.addColorStop(0, rgba(b.color, s.alfa));
            g.addColorStop(s.filo.medio, rgba(b.color, s.alfa * 0.5));
            g.addColorStop(1, rgba(b.color, 0));
            ctx.fillStyle = g;
            ctx.fill();
        });
    },
};
